// Parent Zone — PIN-gated dashboard with overview, allowance, spend limits,
// notes, approvals, and savings-goal boost.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Lock, ShieldCheck, LogOut, Wallet, Send, Trash2, CheckCircle2, XCircle,
  Bell, Target, Sliders, MessageSquare, BarChart3, Flame, Sparkles, Trophy,
  AlertTriangle, Loader2, Save,
} from "lucide-react";
import { Card } from "@/components/primitives";
import { fmtCAD, monthLabel, currentMonth } from "@/lib/budget";
import { familyAPI } from "@/lib/familyApi";

// ---------- PIN Pad ----------
const PinPad = ({ value, onChange, length = 4, disabled }) => {
  const onKey = (e) => {
    if (e.key === "Backspace") onChange(value.slice(0, -1));
    else if (/^\d$/.test(e.key) && value.length < length) onChange(value + e.key);
  };
  return (
    <div
      tabIndex={0}
      onKeyDown={onKey}
      data-testid="pin-pad"
      className="flex items-center gap-2 justify-center mt-4 outline-none focus:ring-2 focus:ring-[#5F7161]/40 rounded-xl py-2"
    >
      {Array.from({ length }).map((_, i) => (
        <div
          key={i}
          className={`w-11 h-12 rounded-xl border-2 flex items-center justify-center font-mono text-xl ${
            value.length > i ? "border-[#5F7161] bg-[#F4F6F1] text-[#1A1C1A]" : "border-[#D8D6D0] bg-white text-[#5A5C58]"
          }`}
        >
          {value.length > i ? "•" : ""}
        </div>
      ))}
      <input
        data-testid="pin-input"
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={length}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, length))}
        disabled={disabled}
        className="sr-only"
        autoFocus
      />
    </div>
  );
};

// ---------- PIN Gate ----------
const PinGate = ({ pinStatus, onUnlock }) => {
  const [pin, setPin] = useState("");
  const [parentName, setParentName] = useState("");
  const [kidName, setKidName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const isSetup = !pinStatus?.pin_set;

  const submit = useCallback(async () => {
    if (pin.length < 4) { setError("Enter a 4-digit PIN"); return; }
    setBusy(true); setError(null);
    try {
      if (isSetup) {
        await familyAPI.setPin({ pin, parent_name: parentName || "Parent", kid_name: kidName || "Kid" });
      } else {
        await familyAPI.verifyPin(pin);
      }
      onUnlock();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not unlock");
      setPin("");
    } finally {
      setBusy(false);
    }
  }, [pin, parentName, kidName, isSetup, onUnlock]);

  // Submit when PIN reaches length
  useEffect(() => {
    if (!isSetup && pin.length === 4 && !busy) submit();
  }, [pin, isSetup, busy, submit]);

  return (
    <Card className="p-8 max-w-lg mx-auto text-center" testId="pin-gate">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#5F7161] text-white flex items-center justify-center mb-3">
        <Lock size={26} />
      </div>
      <h2 className="font-display text-2xl font-semibold">
        {isSetup ? "Set up Parent Zone" : `Welcome back, ${pinStatus?.parent_name || "Parent"}`}
      </h2>
      <p className="text-sm text-[#5A5C58] mt-1 mb-2">
        {isSetup
          ? "Choose a 4-digit PIN. You'll use it to unlock the parent dashboard."
          : "Enter your 4-digit PIN to view the dashboard."}
      </p>

      {isSetup && (
        <div className="mt-4 space-y-2 text-left">
          <div>
            <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Your name</label>
            <input
              data-testid="setup-parent-name"
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              placeholder="Mom / Dad / Grandparent"
              maxLength={40}
              className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Kid&apos;s nickname</label>
            <input
              data-testid="setup-kid-name"
              value={kidName}
              onChange={(e) => setKidName(e.target.value)}
              placeholder="e.g. Aarav, Riya"
              maxLength={40}
              className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
            />
          </div>
        </div>
      )}

      <PinPad value={pin} onChange={setPin} disabled={busy} />

      {error && <p data-testid="pin-error" className="text-sm text-[#B8593E] mt-3">{error}</p>}

      <button
        data-testid="pin-submit"
        onClick={submit}
        disabled={busy || pin.length < 4}
        className="mt-4 bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-50 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-colors"
      >
        {busy ? <Loader2 className="inline animate-spin mr-2" size={14} /> : <ShieldCheck className="inline mr-2 -mt-0.5" size={14} />}
        {isSetup ? "Create PIN & Unlock" : "Unlock"}
      </button>

      <p className="text-[11px] text-[#8A8C88] mt-4">
        Auto-locks after 5 minutes of inactivity. PIN is stored as a hash, never in plain text.
      </p>
    </Card>
  );
};

// ---------- Overview cards ----------
const Stat = ({ label, value, sub, accent = "#5F7161", testId }) => (
  <div data-testid={testId} className="p-4 rounded-xl bg-white border border-[#D8D6D0]">
    <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">{label}</p>
    <p className="font-display text-2xl font-semibold mt-1.5" style={{ color: accent }}>{value}</p>
    {sub && <p className="text-xs text-[#5A5C58] mt-1">{sub}</p>}
  </div>
);

// ---------- Parent Dashboard ----------
const ParentDashboard = ({ onLock, month: initialMonth }) => {
  const [month, setMonth] = useState(initialMonth || currentMonth());
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notes, setNotes] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const [s, sts, n, a] = await Promise.all([
        familyAPI.getSummary(month),
        familyAPI.getSettingsParent(),
        familyAPI.listNotes(),
        familyAPI.listApprovals(),
      ]);
      setSummary(s);
      setSettings(sts);
      setNotes(n.notes || []);
      setApprovals(a.approvals || []);
    } catch (e) {
      if (e?.response?.status === 401) onLock();
      else setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [month, onLock]);

  useEffect(() => { reload(); }, [reload]);

  const handleLogout = async () => {
    await familyAPI.logout();
    onLock();
  };

  // ---- Settings handlers ----
  const updateField = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const updateLimit = (cat, amount) => setSettings((s) => ({
    ...s,
    spend_limits: { ...(s.spend_limits || {}), [cat]: amount },
  }));
  const removeLimit = (cat) => setSettings((s) => {
    const next = { ...(s.spend_limits || {}) };
    delete next[cat];
    return { ...s, spend_limits: next };
  });

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const data = await familyAPI.updateSettings({
        parent_name: settings.parent_name,
        kid_name: settings.kid_name,
        allowance_amount: Number(settings.allowance_amount) || 0,
        allowance_frequency: settings.allowance_frequency,
        spend_limits: Object.fromEntries(
          Object.entries(settings.spend_limits || {})
            .map(([k, v]) => [k, Number(v) || 0])
            .filter(([, v]) => v >= 0)
        ),
      });
      setSettings(data);
      await reload();
    } catch (e) {
      if (e?.response?.status === 401) onLock();
      else setError(e?.response?.data?.detail || e.message);
    } finally {
      setSavingSettings(false);
    }
  };

  // ---- Note add ----
  const addNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    try {
      await familyAPI.addNote(t);
      setNoteText("");
      reload();
    } catch (e) {
      if (e?.response?.status === 401) onLock();
    }
  };

  // ---- Approval resolve ----
  const resolveApproval = async (id, decision) => {
    try {
      await familyAPI.resolveApproval(id, decision, null);
      reload();
    } catch (e) {
      if (e?.response?.status === 401) onLock();
    }
  };

  // ---- Goal boost ----
  const setBoost = async (amount) => {
    try {
      await familyAPI.setGoalBoost(month, Number(amount) || 0);
      reload();
    } catch (e) {
      if (e?.response?.status === 401) onLock();
    }
  };

  const pendingCount = approvals.filter((a) => a.status === "pending").length;
  const unreadNotes = notes.filter((n) => !n.read).length;

  const TABS = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "allowance", label: "Allowance & Limits", icon: Sliders },
    { id: "notes", label: "Notes", icon: MessageSquare, badge: notes.length },
    { id: "approvals", label: "Requests", icon: Bell, badge: pendingCount },
    { id: "goal", label: "Goal Boost", icon: Target },
  ];

  if (loading) {
    return (
      <Card className="p-10 text-center">
        <Loader2 className="inline animate-spin mr-2" size={18} /> Loading family dashboard…
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top bar */}
      <Card className="p-5" testId="parent-zone-topbar">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#6B8E23] text-white flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#5A5C58] font-medium">Parent Zone</p>
              <h2 className="font-display text-xl font-semibold leading-tight">
                Hi {summary?.parent_name || settings?.parent_name || "Parent"} — here&apos;s how {summary?.kid_name || settings?.kid_name || "your kid"} is doing
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="parent-month-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-white border border-[#D8D6D0] rounded-full px-3 py-1.5 text-sm font-medium text-[#1A1C1A] focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40"
            >
              {[-2, -1, 0, 1].map((d) => {
                const [y, m] = currentMonth().split("-").map(Number);
                const dt = new Date(y, m - 1 + d, 1);
                const ym = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
                return <option key={ym} value={ym}>{monthLabel(ym)}</option>;
              })}
            </select>
            <button
              data-testid="parent-lock-btn"
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#D8D6D0] hover:bg-[#F4F6F1] text-[#5A5C58] text-xs font-medium"
            >
              <LogOut size={13} /> Lock
            </button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`parent-subtab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  active ? "bg-[#5F7161] text-white" : "bg-white border border-[#D8D6D0] text-[#5A5C58] hover:bg-[#F4F6F1]"
                }`}
              >
                <Icon size={12} />
                {t.label}
                {t.badge > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${active ? "bg-white text-[#5F7161]" : "bg-[#E27D60] text-white"}`}>{t.badge}</span>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {error && (
        <Card className="p-4 border border-[#F6C5BD] bg-[#FDECEA] text-[#8A1F11] text-sm" testId="parent-zone-error">
          {error}
        </Card>
      )}

      {/* OVERVIEW */}
      {tab === "overview" && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat testId="overview-income"   label="Income"      value={fmtCAD(summary.totals.income)} accent="#5F7161" />
            <Stat testId="overview-expenses" label="Expenditure" value={fmtCAD(summary.totals.expenses)} accent="#E27D60" />
            <Stat testId="overview-savings"  label="Saved"       value={fmtCAD(summary.totals.savings)} accent="#6B8E23" />
            <Stat testId="overview-mentor-xp" label="Mentor XP" value={`${summary.mentor.xp} XP`} sub={`Level ${summary.mentor.level} · ${summary.mentor.streak_days}-day streak`} accent="#8A5A1A" />
          </div>

          <Card className="p-5" testId="overview-top-cats">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Top spending categories</p>
            <div className="mt-3 space-y-2">
              {summary.top_categories.length === 0 && (
                <p className="text-sm text-[#5A5C58]">No expenses logged for {monthLabel(summary.month)} yet.</p>
              )}
              {summary.top_categories.map((c, i) => (
                <div key={c.category} className="flex items-center justify-between p-3 rounded-xl bg-[#F4F6F1] border border-[#E0E5DA]">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-white border border-[#D8D6D0] text-[#5F7161] text-xs font-mono flex items-center justify-center">{i + 1}</span>
                    <span className="font-medium">{c.category}</span>
                  </div>
                  <span className="font-mono text-sm">{fmtCAD(c.amount)}</span>
                </div>
              ))}
            </div>
          </Card>

          {summary.spend_limit_flags.length > 0 && (
            <Card className="p-5" testId="overview-flags">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={14} className="text-[#E27D60]" />
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Spend-limit watch</p>
              </div>
              <div className="space-y-2">
                {summary.spend_limit_flags.map((f) => (
                  <div
                    key={f.category}
                    data-testid={`flag-${f.category.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center justify-between p-3 rounded-xl border ${
                      f.status === "over"
                        ? "bg-[#FDECEA] border-[#F6C5BD]"
                        : f.status === "near"
                        ? "bg-[#FFF4E5] border-[#F4D9A7]"
                        : "bg-[#F4F6F1] border-[#E0E5DA]"
                    }`}
                  >
                    <div>
                      <p className="font-medium text-sm">{f.category}</p>
                      <p className="text-xs text-[#5A5C58]">
                        {fmtCAD(f.spent)} / {fmtCAD(f.cap)} · {f.pct.toFixed(0)}% used
                      </p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      f.status === "over" ? "bg-[#E27D60] text-white" :
                      f.status === "near" ? "bg-[#E2B35B] text-[#1A1C1A]" :
                      "bg-[#6B8E23] text-white"
                    }`}>
                      {f.status === "over" ? "Over" : f.status === "near" ? "Near limit" : "On track"}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-5" testId="overview-mentor">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Money Mentor progress</p>
                <p className="font-display text-xl font-semibold mt-1">
                  Level {summary.mentor.level} · {summary.mentor.xp} XP
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E27D6014] border border-[#E27D6033] text-[#B8593E] text-xs">
                  <Flame size={11} /> {summary.mentor.streak_days}-day
                </span>
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E2B35B14] border border-[#E2B35B55] text-[#8A5A1A] text-xs">
                  <Sparkles size={11} /> {summary.mentor.xp} XP
                </span>
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#5F716114] border border-[#5F716133] text-[#5F7161] text-xs">
                  <Trophy size={11} /> Level {summary.mentor.level}
                </span>
              </div>
            </div>
            {summary.mentor.badges.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {summary.mentor.badges.map((b) => (
                  <span key={b} className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#D8D6D0]">🏆 {b}</span>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {/* ALLOWANCE & LIMITS */}
      {tab === "allowance" && settings && (
        <>
          <Card className="p-5" testId="settings-allowance-card">
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={14} className="text-[#5F7161]" />
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Allowance</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Amount (CAD)</label>
                <input
                  data-testid="allowance-amount-input"
                  type="number" min="0" step="1"
                  value={settings.allowance_amount || 0}
                  onChange={(e) => updateField({ allowance_amount: e.target.value === "" ? 0 : Number(e.target.value) })}
                  className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-right font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Frequency</label>
                <select
                  data-testid="allowance-frequency-select"
                  value={settings.allowance_frequency || "weekly"}
                  onChange={(e) => updateField({ allowance_frequency: e.target.value })}
                  className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5 text-[#5A5C58] uppercase tracking-wider">Kid&apos;s nickname</label>
                <input
                  data-testid="settings-kid-name"
                  value={settings.kid_name || ""}
                  onChange={(e) => updateField({ kid_name: e.target.value })}
                  maxLength={40}
                  className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
                />
              </div>
            </div>
          </Card>

          <Card className="p-5" testId="settings-limits-card">
            <div className="flex items-center gap-2 mb-3">
              <Sliders size={14} className="text-[#5F7161]" />
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Spend limits (soft alerts)</p>
            </div>
            <p className="text-xs text-[#5A5C58] mb-3">
              Set a monthly cap per category. We&apos;ll show your kid a friendly nudge as they approach it — never a hard block.
            </p>
            <div className="space-y-2">
              {Object.entries(settings.spend_limits || {}).map(([cat, amt]) => (
                <div key={cat} className="flex items-center gap-2">
                  <input
                    data-testid={`limit-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    value={cat}
                    readOnly
                    className="flex-1 bg-[#F4F6F1] border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm text-[#1A1C1A]"
                  />
                  <input
                    data-testid={`limit-amt-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    type="number" min="0"
                    value={amt}
                    onChange={(e) => updateLimit(cat, Number(e.target.value) || 0)}
                    className="w-28 bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-right font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
                  />
                  <button onClick={() => removeLimit(cat)} className="w-9 h-9 rounded-xl border border-[#D8D6D0] hover:bg-[#FDECEA] hover:border-[#F6C5BD] text-[#8A1F11] flex items-center justify-center">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <AddLimitRow onAdd={(cat, amt) => updateLimit(cat, amt)} />
          </Card>

          <div className="flex justify-end">
            <button
              data-testid="settings-save-btn"
              onClick={saveSettings}
              disabled={savingSettings}
              className="flex items-center gap-2 bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-50 text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
            >
              {savingSettings ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save changes
            </button>
          </div>
        </>
      )}

      {/* NOTES */}
      {tab === "notes" && (
        <Card className="p-5" testId="notes-card">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare size={14} className="text-[#5F7161]" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Leave a note for your kid</p>
            {unreadNotes > 0 && (
              <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-[#E27D60] text-white">
                {unreadNotes} unread by kid
              </span>
            )}
          </div>
          <div className="flex items-end gap-2 mb-4">
            <textarea
              data-testid="note-input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={2} maxLength={240}
              placeholder="e.g. Great job staying under groceries this week! Pizza Friday 🍕"
              className="flex-1 resize-none bg-white border border-[#D8D6D0] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
            />
            <button
              data-testid="note-send-btn"
              onClick={addNote}
              disabled={!noteText.trim()}
              className="bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center"
              aria-label="Send note"
            >
              <Send size={16} />
            </button>
          </div>
          <div className="space-y-2">
            {notes.length === 0 && (
              <p className="text-sm text-[#5A5C58]">No notes yet. Your first note will show up on your kid&apos;s home.</p>
            )}
            {notes.map((n) => (
              <div key={n.id} data-testid={`note-${n.id}`} className="p-3 rounded-xl bg-[#F4F6F1] border border-[#E0E5DA] flex items-start gap-2">
                <div className="flex-1">
                  <p className="text-sm">{n.text}</p>
                  <p className="text-[10px] text-[#8A8C88] mt-1">
                    {new Date(n.created_at).toLocaleString()}
                    {n.read ? " · ✓ seen by kid" : " · waiting to be read"}
                  </p>
                </div>
                <button
                  onClick={async () => { await familyAPI.deleteNote(n.id); reload(); }}
                  className="text-[#8A1F11] hover:bg-[#FDECEA] rounded-lg w-8 h-8 flex items-center justify-center"
                  aria-label="Delete note"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* APPROVALS */}
      {tab === "approvals" && (
        <Card className="p-5" testId="approvals-card">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={14} className="text-[#5F7161]" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Approval requests from your kid</p>
          </div>
          <div className="space-y-2">
            {approvals.length === 0 && (
              <p className="text-sm text-[#5A5C58]">No requests yet. When your kid taps &quot;Ask Mom/Dad&quot;, you&apos;ll see them here.</p>
            )}
            {approvals.map((a) => (
              <div key={a.id} data-testid={`approval-${a.id}`} className="p-3 rounded-xl border bg-white border-[#D8D6D0]">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium">
                      {fmtCAD(a.amount)} {a.category ? <span className="text-[#5A5C58] font-normal">· {a.category}</span> : null}
                    </p>
                    <p className="text-xs text-[#5A5C58] mt-0.5">{a.reason}</p>
                    <p className="text-[10px] text-[#8A8C88] mt-1">{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                  {a.status === "pending" ? (
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`approve-${a.id}`}
                        onClick={() => resolveApproval(a.id, "approved")}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#6B8E23] hover:bg-[#557018] text-white text-xs font-medium"
                      >
                        <CheckCircle2 size={12} /> Approve
                      </button>
                      <button
                        data-testid={`deny-${a.id}`}
                        onClick={() => resolveApproval(a.id, "denied")}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#D8D6D0] hover:bg-[#FDECEA] hover:border-[#F6C5BD] text-[#8A1F11] text-xs font-medium"
                      >
                        <XCircle size={12} /> Deny
                      </button>
                    </div>
                  ) : (
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      a.status === "approved" ? "bg-[#EDF3E0] text-[#3D5B0F]" : "bg-[#FDECEA] text-[#8A1F11]"
                    }`}>
                      {a.status === "approved" ? "✓ Approved" : "✗ Denied"}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* GOAL BOOST */}
      {tab === "goal" && summary && (
        <Card className="p-5" testId="goal-boost-card">
          <div className="flex items-center gap-2 mb-3">
            <Target size={14} className="text-[#6B8E23]" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#5A5C58] font-medium">Boost your kid&apos;s savings goal for {monthLabel(month)}</p>
          </div>
          <p className="text-sm text-[#5A5C58] mb-4">
            Match a portion of what your kid saves. It shows up as a parent-boost bar next to their goal.
          </p>
          <div className="flex items-center gap-2">
            <input
              data-testid="goal-boost-input"
              type="number" min="0"
              defaultValue={summary.goal_boost_this_month || 0}
              onBlur={(e) => setBoost(e.target.value)}
              className="w-40 bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-right font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
            />
            <span className="text-sm text-[#5A5C58]">CAD boost this month</span>
          </div>
          {summary.goal_boost_this_month > 0 && (
            <p className="text-xs text-[#3D5B0F] mt-3 bg-[#EDF3E0] border border-[#B6D77E] rounded-xl px-3 py-2 inline-block">
              ✨ You&apos;re boosting {fmtCAD(summary.goal_boost_this_month)} this month. Your kid sees this on their home.
            </p>
          )}
        </Card>
      )}
    </div>
  );
};

const AddLimitRow = ({ onAdd }) => {
  const [cat, setCat] = useState("");
  const [amt, setAmt] = useState("");
  const add = () => {
    const c = cat.trim();
    if (!c) return;
    onAdd(c, Number(amt) || 0);
    setCat(""); setAmt("");
  };
  return (
    <div className="mt-3 flex items-center gap-2" data-testid="add-limit-row">
      <input
        value={cat}
        onChange={(e) => setCat(e.target.value)}
        placeholder="New category (e.g. Snacks)"
        className="flex-1 bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
        data-testid="add-limit-cat"
      />
      <input
        type="number" min="0" value={amt} placeholder="$"
        onChange={(e) => setAmt(e.target.value)}
        className="w-28 bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-right font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
        data-testid="add-limit-amt"
      />
      <button
        onClick={add}
        className="px-3 py-2 rounded-xl bg-[#5F7161] hover:bg-[#4E5C50] text-white text-sm font-medium"
        data-testid="add-limit-btn"
      >
        Add
      </button>
    </div>
  );
};

// ---------- Root ----------
const ParentZone = ({ month }) => {
  const [pinStatus, setPinStatus] = useState(null);
  const [unlocked, setUnlocked] = useState(() => !!familyAPI.getToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    familyAPI.pinStatus().then((s) => { setPinStatus(s); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  // Auto-lock when token expires
  useEffect(() => {
    if (!unlocked) return;
    const t = setInterval(() => {
      if (!familyAPI.getToken()) setUnlocked(false);
    }, 10000);
    return () => clearInterval(t);
  }, [unlocked]);

  if (loading) {
    return (
      <Card className="p-10 text-center">
        <Loader2 className="inline animate-spin mr-2" size={18} /> Loading Parent Zone…
      </Card>
    );
  }

  if (!unlocked) {
    return (
      <PinGate
        pinStatus={pinStatus}
        onUnlock={() => setUnlocked(true)}
      />
    );
  }

  return <ParentDashboard onLock={() => setUnlocked(false)} month={month} />;
};

export default ParentZone;
